/**
 * INTERNAL SWAP MODAL - No Redirects
 * Swap SOL ↔ $SLAP directly in-app using Jupiter API
 * 1% platform fee → Treasury (GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa)
 * SUPPORTS BOTH BUY AND SELL!
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowDownUp, Loader2, Check, AlertTriangle, Wallet, ArrowUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { executeRealSwap, executeSellSwap, getSwapEstimate, getSellEstimate } from '../services/jupiterSwapService';
import { useGameWallet } from '../providers/GameWalletProvider';
import { useAuth } from '../providers/AuthProvider';

const TREASURY_WALLET = 'GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa';
const MIN_SWAP_SOL = 0.005;
const MIN_SWAP_SLAP = 100; // Minimum $SLAP to sell
// Use PUBLIC Solana mainnet (no Helius)
const RPC_URL = 'https://api.mainnet-beta.solana.com';

const InternalSwapModal = ({ isOpen, onClose }) => {
  const { solBalance, slapBalance, fetchSolBalance, fetchSlapTokenBalance } = useGameWallet();
  const { solanaAddress, secretKey } = useAuth();
  
  const [inputAmount, setInputAmount] = useState('');
  const [swapDirection, setSwapDirection] = useState('buy'); // 'buy' (SOL→SLAP) or 'sell' (SLAP→SOL)
  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [swapProgress, setSwapProgress] = useState(null);
  const [swapComplete, setSwapComplete] = useState(false);
  const [lastSignature, setLastSignature] = useState(null);

  // Get current balance based on swap direction
  const currentBalance = swapDirection === 'buy' ? solBalance : slapBalance;
  const inputToken = swapDirection === 'buy' ? 'SOL' : '$SLAP';
  const outputToken = swapDirection === 'buy' ? '$SLAP' : 'SOL';
  const minAmount = swapDirection === 'buy' ? MIN_SWAP_SOL : MIN_SWAP_SLAP;

  // Fetch estimate when amount changes
  useEffect(() => {
    if (!inputAmount || parseFloat(inputAmount) < minAmount) {
      setEstimate(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        if (swapDirection === 'buy') {
          const est = await getSwapEstimate(parseFloat(inputAmount));
          setEstimate(est);
        } else {
          // Get real sell estimate from Jupiter
          const est = await getSellEstimate(parseFloat(inputAmount));
          if (est) {
            setEstimate({
              outputTokens: est.netSol.toFixed(6),
              route: est.route,
              feeSol: est.feeSol
            });
          } else {
            setEstimate(null);
          }
        }
      } catch (e) {
        console.error('Estimate error:', e);
        setEstimate(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [inputAmount, swapDirection, minAmount]);

  // Toggle swap direction
  const toggleDirection = () => {
    setSwapDirection(prev => prev === 'buy' ? 'sell' : 'buy');
    setInputAmount('');
    setEstimate(null);
  };

  // Quick amount buttons
  const setQuickAmount = (percent) => {
    if (!currentBalance || currentBalance <= 0) return;
    const amount = swapDirection === 'buy' 
      ? (currentBalance * percent / 100).toFixed(4)
      : Math.floor(currentBalance * percent / 100);
    setInputAmount(String(amount));
  };

  // Execute swap
  const handleSwap = async () => {
    const amount = parseFloat(inputAmount);

    if (!amount || amount < minAmount) {
      toast.error(`Minimum swap is ${minAmount} ${inputToken}`);
      return;
    }

    if (amount > currentBalance) {
      toast.error(`Insufficient ${inputToken} balance`);
      return;
    }

    if (!secretKey) {
      toast.error('Wallet key not found');
      return;
    }

    setIsLoading(true);
    setSwapProgress({ step: 'starting', message: 'Initializing swap...' });

    try {
      if (swapDirection === 'buy') {
        // BUY: SOL → $SLAP
        const result = await executeRealSwap({
          solAmount: amount,
          userPublicKey: solanaAddress,
          secretKeyBase58: secretKey,
          rpcUrl: RPC_URL,
          slippageBps: 150,
          onProgress: setSwapProgress
        });

        if (result.success) {
          setLastSignature(result.signature);
          setSwapComplete(true);
          
          await fetchSolBalance(true);
          await fetchSlapTokenBalance(true);

          toast.success('Swap Complete! 🎉', {
            description: `Received ${result.outputAmount?.toLocaleString() || '~'} $SLAP`
          });
        }
      } else {
        // SELL: $SLAP → SOL (via Jupiter)
        setSwapProgress({ step: 'preparing', message: 'Preparing sell order...' });
        
        const result = await executeSellSwap({
          slapAmount: amount,
          userPublicKey: solanaAddress,
          secretKeyBase58: secretKey,
          rpcUrl: RPC_URL,
          slippageBps: 150,
          onProgress: setSwapProgress
        });

        if (result.success) {
          setLastSignature(result.signature);
          setSwapComplete(true);
          
          await fetchSolBalance(true);
          await fetchSlapTokenBalance(true);

          toast.success('Sell Complete! 🎉', {
            description: `Received ${result.outputAmount?.toFixed(6) || '~'} SOL`
          });
        }
      }
    } catch (error) {
      console.error('Swap error:', error);
      toast.error('Swap Failed', {
        description: error.message || 'Please try again'
      });
      setSwapProgress(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset modal
  const handleClose = () => {
    setInputAmount('');
    setEstimate(null);
    setSwapProgress(null);
    setSwapComplete(false);
    setLastSignature(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="relative w-full max-w-md bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl border-2 border-neon-pink/50 overflow-hidden"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <ArrowDownUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-anton text-xl text-white">
                  {swapDirection === 'buy' ? 'BUY $SLAP' : 'SELL $SLAP'}
                </h2>
                <p className="text-xs text-white/50">1% fee → Treasury</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Success State */}
            {swapComplete ? (
              <motion.div
                className="text-center py-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <motion.div
                  className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1, repeat: 2 }}
                >
                  <Check className="w-10 h-10 text-green-400" />
                </motion.div>
                <h3 className="font-anton text-2xl text-green-400 mb-2">SWAP SUCCESSFUL!</h3>
                <p className="text-white/60 text-sm mb-4">
                  Tokens added to your wallet
                </p>
                {lastSignature && (
                  <a
                    href={`https://solscan.io/tx/${lastSignature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 text-sm hover:underline"
                  >
                    View on Solscan →
                  </a>
                )}
                <Button
                  onClick={handleClose}
                  className="mt-6 w-full bg-green-500 hover:bg-green-400 text-white font-bold"
                >
                  DONE
                </Button>
              </motion.div>
            ) : (
              <>
                {/* Swap Direction Toggle */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setSwapDirection('buy')}
                    className={`px-6 py-3 rounded-xl font-bold transition-all ${
                      swapDirection === 'buy'
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    BUY $SLAP
                  </button>
                  
                  <button
                    onClick={toggleDirection}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    title="Switch direction"
                  >
                    <ArrowUpDown className="w-5 h-5 text-white" />
                  </button>
                  
                  <button
                    onClick={() => setSwapDirection('sell')}
                    className={`px-6 py-3 rounded-xl font-bold transition-all ${
                      swapDirection === 'sell'
                        ? 'bg-red-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    SELL $SLAP
                  </button>
                </div>

                {/* Balance Display */}
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/50 text-sm">Your {inputToken} Balance</span>
                    <span className="text-white font-bold">
                      {swapDirection === 'buy' 
                        ? currentBalance?.toFixed(4) || '0'
                        : Math.floor(currentBalance || 0).toLocaleString()
                      } {inputToken}
                    </span>
                  </div>
                </div>

                {/* Input Field */}
                <div className="space-y-3">
                  <label className="text-white/70 text-sm">Amount to {swapDirection === 'buy' ? 'Spend' : 'Sell'} ({inputToken})</label>
                  <input
                    type="number"
                    value={inputAmount}
                    onChange={(e) => setInputAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={isLoading}
                    className="w-full px-4 py-4 bg-black/50 border-2 border-white/20 rounded-xl text-white text-2xl font-bold focus:border-neon-pink focus:outline-none disabled:opacity-50"
                  />
                  
                  {/* Quick Buttons */}
                  <div className="flex gap-2">
                    {[25, 50, 75, 100].map((pct) => (
                      <button
                        key={pct}
                        onClick={() => setQuickAmount(pct)}
                        disabled={isLoading || !currentBalance}
                        className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 text-sm font-bold transition-colors disabled:opacity-50"
                      >
                        {pct === 100 ? 'MAX' : `${pct}%`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Estimate */}
                {estimate && (
                  <motion.div
                    className={`border rounded-xl p-4 space-y-2 ${
                      swapDirection === 'buy'
                        ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30'
                        : 'bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30'
                    }`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">You'll receive</span>
                      <span className={`font-bold ${swapDirection === 'buy' ? 'text-green-400' : 'text-yellow-400'}`}>
                        ~{swapDirection === 'buy' 
                          ? estimate.outputTokens?.toLocaleString() || '0'
                          : parseFloat(estimate.outputTokens || 0).toFixed(6)
                        } {outputToken}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Platform Fee (1%)</span>
                      <span className="text-yellow-400">{estimate.feeSol?.toFixed(4)} SOL</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Route</span>
                      <span className="text-white/60">{estimate.route || 'Jupiter'}</span>
                    </div>
                  </motion.div>
                )}

                {/* Progress */}
                {swapProgress && (
                  <motion.div
                    className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                      <span className="text-purple-300 text-sm">{swapProgress.message}</span>
                    </div>
                  </motion.div>
                )}

                {/* Swap Button */}
                <Button
                  onClick={handleSwap}
                  disabled={isLoading || !inputAmount || parseFloat(inputAmount) < minAmount}
                  className={`w-full py-6 text-white font-anton text-xl rounded-xl disabled:opacity-50 ${
                    swapDirection === 'buy'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
                      : 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {swapDirection === 'buy' ? 'BUYING...' : 'SELLING...'}
                    </div>
                  ) : (
                    swapDirection === 'buy' ? 'BUY $SLAP NOW' : 'SELL $SLAP NOW'
                  )}
                </Button>

                {/* Warning */}
                <div className="flex items-start gap-2 text-yellow-400/70 text-xs">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Swap happens on-chain via Jupiter. 1% fee goes to Treasury: {TREASURY_WALLET.slice(0, 8)}...
                  </span>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InternalSwapModal;
